/**
 * THE PACING OF THE VERIFICATION BEAT — pure, so the presentation layer can be
 * re-skinned without anyone having to reason about time.
 *
 * Client ruling (Brandon, first live use): the checks resolved in about two
 * seconds and "we didn't even get the full effect". Second ruling, after seeing
 * it: make it a spectacle, drop the report list from this screen entirely
 * ("that's on the next screen in the math checks out view details"), and "if we
 * need to add a few seconds, so be it."
 *
 * So Act 1 is now a brand graphic plus ONE cycling phase line, and the full
 * receipt only ever appears in Act 2's collapsed disclosure.
 *
 * THE HONESTY RULE, RESTATED FOR THE NEW SHAPE. The phase lines are statements
 * about what the verification DOES — "Tracing every line to its source…" — and
 * they may cycle on their own clock. They are not verdicts and never claim a
 * result. The RESOLUTION is the only thing that renders from real data. And a
 * FAILURE is never paced at all: it resolves immediately, because padding a
 * refusal with theatre would be the screen performing confidence it does not
 * have.
 *
 * TWO CLOCKS, ONE RING (client, 2026-08-06: "it should be the same thing every
 * time"). The ring used to have nothing to show until the preview came back —
 * it sat at exactly 0 for the length of the request, which is where a
 * round-capped arc renders as a bare dot, and the dot rode the card's ambient
 * rotation. THAT was the reported bug, and it is the whole of it: the staging
 * also discounted itself by the request time, but on the shipped six-line check
 * set that subtraction was swallowed by the MIN_PHASE_MS clamp and changed
 * nothing. It was wrong-but-dormant, priced on a ring that was not in fact
 * filling during the fetch, and it would have started biting the moment the
 * check list got shorter.
 *
 * So the run is now two clocks that hand off:
 *   1. THE CREEP (`creepProgress`) runs from the first frame while the request
 *      is in flight, easing toward a cap it never reaches. It absorbs the wait.
 *   2. THE REVEAL (`planVerificationReveal` + `revealProgress`) starts where the
 *      creep left the ring (`startProgress`) and runs the SAME duration on every
 *      send, fast server or slow. The elapsed-time shortening is gone with it.
 *
 * Neither clock may ever hand the ring a smaller number than it last showed —
 * see `monotonicProgress`.
 *
 * Reduced motion collapses all of it to zero, creep included.
 */
import type { VerificationCheckId } from '@coffman-tm/shared';

/**
 * Floor for the staged reveal — the part that runs AFTER the verification
 * arrives. The client asked for a spectacle and said to add a few seconds if
 * needed; this gives the graphic room to be watched and fits ~5-6 phase lines
 * at a readable cadence.
 *
 * It no longer has the request time subtracted from it (2026-08-06). The creep
 * covers the request; this covers the reveal; and the reveal is therefore the
 * same length on every send.
 *
 * IT IS A MINIMUM, NOT A TOTAL, and the shipped run is already past it: six
 * phase lines divide this into 883ms each, under `MIN_PHASE_MS`, so the clamp
 * wins and the real run is 5400 + 1900 = 7300ms.
 */
export const MIN_BEAT_MS = 7200;
/**
 * How long "The math checks out" holds before the composer takes over.
 *
 * THIS IS THE PAYOFF, AND IT IS DELIBERATELY THE LONGEST SINGLE MOMENT IN THE
 * RUN. Third client ruling (Brandon, live): "i need a more satisfying yes its
 * all green" — the ramp was eating the beat and the resolve flashed past. The
 * fix was to REDISTRIBUTE, not extend: the phase copy below merges two pairs of
 * checks so the ramp is six lines instead of eight, and that reclaimed time
 * went here. Total run is slightly SHORTER than before.
 *
 * If you ever add phase lines back, take the time from the runway, not from
 * this. The green is the thing he is buying.
 */
export const RESOLVE_HOLD_MS = 1900;
/** Each phase line stays up at least this long, or it cannot be read. */
export const MIN_PHASE_MS = 900;
/**
 * …and no longer than this, or the run stalls. Held BELOW `RESOLVE_HOLD_MS` on
 * purpose: no phase line may ever dwell longer than the green does, including
 * on a short verification with only one or two lines to show.
 */
export const MAX_PHASE_MS = 1800;

// ---------------------------------------------------------------------------
// THE CREEP — the ring while the preview request is still in flight
// ---------------------------------------------------------------------------

/**
 * Where the ring stands on the very first frame.
 *
 * NEVER ZERO, and that is the whole point of the constant. At exactly 0 a
 * round-capped arc still paints its cap — a dot — and the card's ambient
 * rotation carries it around the circle, which is what the client saw as "a
 * tiny dot circling". A short arc reads as a ring that has started; a dot reads
 * as a bug. Anything under ~3% is back to a dot at this radius.
 */
export const CREEP_START = 0.045;
/**
 * The ceiling the creep eases toward and never reaches.
 *
 * Held well short of the ring so the reveal has somewhere to go: whatever the
 * server took, the staged beat still gets two thirds of the circle to play in.
 */
export const CREEP_CAP = 0.35;
/**
 * Time to cover HALF the distance from `CREEP_START` to `CREEP_CAP`.
 *
 * Deliberately short so the movement is obvious inside the first few hundred
 * milliseconds — a 400ms server has to look like it did something. The curve is
 * rational rather than exponential because an exponential tail is visually dead
 * by ~3s, and a 6s cold Worker still has to look alive.
 */
export const CREEP_HALF_MS = 900;

/**
 * Ring fill during the wait, 0..1 — pure, so the panel owns no curve of its own.
 *
 * Monotonic, starts at `CREEP_START`, asymptotic to `CREEP_CAP`. It is NOT an
 * estimate of how far the server has got and never claims to be: it is the
 * screen saying "still working", which is exactly what it knows.
 */
export function creepProgress(elapsedMs: number): number {
  const t = Math.max(0, elapsedMs);
  return CREEP_START + (CREEP_CAP - CREEP_START) * (t / (t + CREEP_HALF_MS));
}

/**
 * The monotonicity guarantee, applied to whatever the two clocks produce.
 *
 * A progress ring may never step backwards. The handoff is where it would: the
 * creep's clock stops and the reveal's starts, and the two are measured from
 * different instants. `startProgress` already makes them meet; this makes it
 * true under sub-frame skew as well.
 */
export function monotonicProgress(shown: number, next: number): number {
  return Math.max(clamp(shown, 0, 1), clamp(next, 0, 1));
}

/**
 * What each check is DOING, in the PM's language. Phase copy, not verdicts —
 * these describe the work, so they stay honest whatever the outcome turns out
 * to be.
 *
 * Derived from the checks a verification actually contains, so a first send
 * never claims to be "measuring this revision against the last send".
 */
// "Excavating the numbers…" is the client's own line, approved verbatim, and it
// sets the register for the rest: the crew's vocabulary, used straight. Each one
// still names a REAL phase — re-adding the workbook, tracing provenance,
// checking the package — so nothing here is decoration.
//
// The bar every line has to clear: a GC could read it over Joey's shoulder and
// think "yes, that's what they're doing." No emoji, no puns, nothing that would
// embarrass him on a shared screen.
//
// A PAIR SHARES A LINE ON PURPOSE (client, third pass). Eight lines made the
// ramp so long the green resolve read as a flicker. Rather than speed the ramp
// up past legibility, checks that are doing the SAME KIND OF WORK now narrate
// under one line, and `phasesForChecks` de-duplicates them. Six lines up, the
// reclaimed ~1.8s spent on the payoff.
//
// The merge has to stay honest — a shared line must be true of BOTH checks:
//   ladder_identity + revision_rollforward → both ask whether the totals agree
//     with what they are supposed to be (this week's ladder; the last send).
//
// The second merge (expected_manual_adds + materials_enumerated) went with the
// hand-entered-work check on 2026-08-13; materials keeps the line on its own,
// and the run is still six lines because the surviving merge does the work.
const PHASE_COPY: Record<VerificationCheckId, string> = {
  workbook_readback: 'Excavating the numbers…',
  ladder_identity: 'Squaring the totals…',
  provenance: 'Surveying every line…',
  attachments: 'Staging the paperwork…',
  revision_rollforward: 'Squaring the totals…',
  materials_enumerated: 'Walking the hand-entered work…',
  independent_reread: 'Second set of eyes on the dailies…',
};

/** Shown when a verification arrives with no recognisable checks. */
export const FALLBACK_PHASE = 'Checking this week’s numbers…';

/**
 * The phase lines for a run, in check order, de-duplicated. Pure — the panel
 * hands in the check ids it actually received.
 */
export function phasesForChecks(checkIds: readonly VerificationCheckId[]): string[] {
  const out: string[] = [];
  for (const id of checkIds) {
    const copy = PHASE_COPY[id];
    if (copy && !out.includes(copy)) out.push(copy);
  }
  return out.length > 0 ? out : [FALLBACK_PHASE];
}

export interface RevealPlan {
  /** ms after the plan starts at which each phase line becomes the active one. */
  phaseAtMs: number[];
  /** ms at which the graphic settles and the verdict renders. */
  resolveAtMs: number;
  /** ms at which the composer takes over. NULL when the send is blocked. */
  handoffAtMs: number | null;
  /** End of the beat — the handoff, or the resolve when there is none. */
  totalMs: number;
  /**
   * Ring fill the reveal INHERITS from the creep, 0..1. The staged fill runs
   * from here to 1 rather than from 0, so the handoff cannot dip.
   */
  startProgress: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Build the schedule for everything AFTER the verification lands.
 *
 * THE SCHEDULE DOES NOT KNOW HOW LONG THE REQUEST TOOK, on purpose (2026-08-06).
 * It used to: the runway was shortened by `elapsedMs`, on the reasoning that the
 * ring had been turning during the fetch and that time counted. It hadn't — the
 * ring had nothing to fill from until the plan existed — so the discount was
 * priced on a premise that was not true. It was also, on the six-line check set
 * we actually ship, INERT: 5300/6 = 883ms is already under `MIN_PHASE_MS`, so
 * the clamp pinned every phase at 900ms whatever the server had taken. Five
 * lines or fewer is where it would have shown. Deleting it costs nothing today
 * and makes the guarantee hold for a check list of any length.
 *
 * The creep now genuinely covers the wait, and it hands over its position
 * through `startProgress` rather than through a discount on the beat.
 */
export function planVerificationReveal(args: {
  phaseCount: number;
  /** True when any check failed. */
  blocked: boolean;
  reducedMotion: boolean;
  /** Where the creep left the ring, 0..1. Omitted = no creep ran. */
  startProgress?: number;
}): RevealPlan {
  const { phaseCount, blocked, reducedMotion } = args;
  const startProgress = clamp(args.startProgress ?? 0, 0, 1);

  // A FAILURE IS NEVER PACED. No cycle, no padding — the screen shows the
  // refusal the moment it knows about it. Theatre in front of a blocked send
  // would be the screen performing confidence it does not have.
  if (blocked || reducedMotion) {
    return {
      phaseAtMs: new Array(Math.max(0, phaseCount)).fill(0),
      resolveAtMs: 0,
      handoffAtMs: blocked ? null : 0,
      totalMs: 0,
      startProgress,
    };
  }

  const runway = MIN_BEAT_MS - RESOLVE_HOLD_MS;
  const n = Math.max(1, phaseCount);
  const perPhase = clamp(runway / n, MIN_PHASE_MS, MAX_PHASE_MS);
  const phaseAtMs = Array.from({ length: phaseCount }, (_, i) => Math.round(perPhase * i));
  const resolveAtMs = Math.round(perPhase * n);
  const handoffAtMs = resolveAtMs + RESOLVE_HOLD_MS;

  return { phaseAtMs, resolveAtMs, handoffAtMs, totalMs: handoffAtMs, startProgress };
}

/**
 * Ring fill during the staged reveal, 0..1. Reaches 1 exactly when the last
 * phase ends — the graphic is a readout of the cycle, not a second clock that
 * could disagree with it.
 *
 * It starts at `plan.startProgress`, not at 0: the creep already put the ring
 * there and taking it back would be the one thing a progress ring may not do.
 * The remaining arc is what gets staged.
 */
export function revealProgress(plan: RevealPlan, nowMs: number): number {
  if (plan.resolveAtMs <= 0) return 1;
  const start = clamp(plan.startProgress, 0, 1);
  return start + (1 - start) * clamp(nowMs / plan.resolveAtMs, 0, 1);
}

/** Index of the phase line showing at a point in the beat. */
export function activePhaseIndex(plan: RevealPlan, nowMs: number): number {
  let idx = 0;
  for (let i = 0; i < plan.phaseAtMs.length; i += 1) {
    if (nowMs >= plan.phaseAtMs[i]!) idx = i;
  }
  return idx;
}
